import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import axios from "axios";
import { Link2, Printer, FolderClosed, FileText, FileDown, FileArchive, Slack, ArrowLeft, ChevronDown, Hash, Lock, AtSign, Check, AlertTriangle } from "lucide-react";
import { LucideIcon } from "./icon-registry";
import { useModalDismiss } from "./useModalDismiss";

type EntityKind = "page" | "category" | "folder";

interface Recipient {
    id: string;
    type: "channel" | "user";
    displayName: string;
    isPrivate: boolean;
    isExternal: boolean;
}

interface Props {
    kind: EntityKind;
    entityId?: string;
    label: string;
    url: string;
    iconName?: string | null;
    iconColor?: string | null;
    isRestricted?: boolean;
    onClose: () => void;
}

type Panel = "options" | "slack";

export default function ShareModal({ kind, entityId, label, url, iconName, iconColor, isRestricted, onClose }: Props) {
    const { show, dismiss, onExited } = useModalDismiss(onClose);
    const [panel, setPanel] = useState<Panel>("options");

    const [slackConfigured, setSlackConfigured] = useState<boolean | null>(null);
    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [recipientFilter, setRecipientFilter] = useState("");
    const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [sharing, setSharing] = useState(false);
    // armed once the user clicks Share with an external recipient; a second,
    // explicit click confirms the external share
    const [awaitingExternalConfirm, setAwaitingExternalConfirm] = useState(false);
    // for a selected channel: whether it contains members outside the organization
    const [channelHasExternal, setChannelHasExternal] = useState(false);
    const pickerRef = useRef<HTMLDivElement | null>(null);

    // a share "exposes externally" if the recipient itself is external (Slack Connect
    // channel / guest user) or if the selected channel includes outside members
    const externalExposure = !!selectedRecipient && (selectedRecipient.isExternal || channelHasExternal);

    const titleByKind = { page: "Share page", category: "Share category", folder: "Share folder" }[kind];

    const handleCopy = () => {
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(url)
                .then(() => { toast.success("Link copied"); dismiss("cancel"); })
                .catch(() => toast.error("Failed to copy link"));
        } else {
            toast.error("Clipboard not available");
        }
    };

    const handlePrint = () => {
        dismiss("cancel");
        setTimeout(() => window.print(), 250);
    };

    const handleDownload = (href: string) => {
        dismiss("cancel");
        setTimeout(() => window.location.assign(href), 100);
    };

    const openSlack = () => {
        setPanel("slack");
        if (slackConfigured === null) {
            setLoadingRecipients(true);
            axios.get("/api/manuals/slack/recipients")
                .then(res => {
                    if (res.data?.success) {
                        setSlackConfigured(!!res.data.configured);
                        setRecipients(res.data.items || []);
                    } else {
                        setSlackConfigured(false);
                    }
                })
                .catch(() => setSlackConfigured(false))
                .finally(() => setLoadingRecipients(false));
        }
    };

    const handleShareSlack = () => {
        if (!entityId || kind !== "page" || !selectedRecipient) return;
        // sharing that reaches outside the org requires an explicit confirm first
        if (externalExposure && !awaitingExternalConfirm) {
            setAwaitingExternalConfirm(true);
            return;
        }
        setSharing(true);
        axios.post(`/api/manuals/pages/${entityId}/share-slack`, {
            recipientId: selectedRecipient.id,
            recipientName: selectedRecipient.displayName,
            message: message.trim() || null,
        })
            .then(res => {
                if (res.data?.success) {
                    toast.success(`Shared to ${selectedRecipient.displayName}`);
                    dismiss("cancel");
                } else {
                    toast.error(res.data?.message || "Failed to share");
                }
            })
            .catch(err => {
                const msg = err?.response?.data?.message || "Failed to share to Slack";
                toast.error(msg);
            })
            .finally(() => setSharing(false));
    };

    useEffect(() => {
        if (!pickerOpen) return;
        const onDocClick = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [pickerOpen]);

    // when a channel is selected, check whether it contains members outside the org.
    // (users are handled by the recipient-level isExternal flag.)
    useEffect(() => {
        setChannelHasExternal(false);
        if (!selectedRecipient || selectedRecipient.type !== "channel") return;
        let cancelled = false;
        axios.get(`/api/manuals/slack/channels/${selectedRecipient.id}/external`)
            .then(res => {
                if (!cancelled && res.data?.success) setChannelHasExternal(!!res.data.hasExternalMembers);
            })
            .catch(() => { /* best-effort; absence of the flag just means no extra warning */ });
        return () => { cancelled = true; };
    }, [selectedRecipient]);

    const filteredRecipients = useMemo(() => {
        const q = recipientFilter.trim().toLowerCase();
        if (!q) return recipients;
        return recipients.filter(r => r.displayName.toLowerCase().includes(q));
    }, [recipients, recipientFilter]);

    const channelGroup = filteredRecipients.filter(r => r.type === "channel" && !r.isPrivate);
    const privateChannelGroup = filteredRecipients.filter(r => r.type === "channel" && r.isPrivate);
    const userGroup = filteredRecipients.filter(r => r.type === "user");

    const swatchBg = iconColor
        || (kind === "page" ? "#1e85e5" : kind === "category" ? "#3B82F6" : "#64748b");

    const swatchGlyph = kind === "page"
        ? <FileText size={14} color="#fff" />
        : <LucideIcon name={iconName || (kind === "folder" ? "FolderClosed" : "FolderClosed")} size={14} color="#fff" />;

    const renderRecipientRow = (r: Recipient) => {
        const icon = r.type === "user"
            ? <AtSign size={14} />
            : r.isPrivate
                ? <Lock size={14} />
                : <Hash size={14} />;
        const isSelected = selectedRecipient?.id === r.id;
        return (
            <button
                key={r.id}
                type="button"
                className={`m-slack-recipient-row${isSelected ? " is-selected" : ""}`}
                onClick={() => { setSelectedRecipient(r); setPickerOpen(false); setRecipientFilter(""); setAwaitingExternalConfirm(false); }}
            >
                <span className="m-slack-recipient-icon">{icon}</span>
                <span className="m-slack-recipient-name">{r.displayName.replace(/^[@#]/, "")}</span>
                {isSelected && <Check size={14} className="m-slack-recipient-check" />}
            </button>
        );
    };

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered scrollable backdropClassName="m-modal-backdrop">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 17, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                    {panel === "slack" && (
                        <button
                            type="button"
                            className="m-btn m-btn-ghost"
                            style={{ padding: 2, lineHeight: 0 }}
                            onClick={() => { setPanel("options"); setSelectedRecipient(null); setMessage(""); setPickerOpen(false); setAwaitingExternalConfirm(false); }}
                            aria-label="Back"
                        >
                            <ArrowLeft size={16} />
                        </button>
                    )}
                    {titleByKind}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="m-share-entity">
                    <span className="m-share-entity-icon" style={{ background: swatchBg }}>
                        {swatchGlyph}
                    </span>
                    <span className="m-share-entity-label">{label}</span>
                </div>

                {isRestricted && (
                    <div className="m-limited-banner" style={{ margin: "0 0 14px" }}>
                        <span className="m-limited-banner-lead">
                            <Lock size={13} />
                            <strong>This {kind} is limited access.</strong>
                        </span>
                        <span>
                            Please check who has access to this content before sharing. People without access
                            can’t open the link or Slack message — but downloaded copies (PDF, Markdown, ZIP) can be
                            viewed by anyone.
                        </span>
                    </div>
                )}

                {panel === "options" && (
                    <div className="m-share-options">
                        <button type="button" className="m-share-card" onClick={handleCopy}>
                            <Link2 size={20} />
                            <span className="m-share-card-label">Copy link</span>
                            <span className="m-share-card-sub">Paste anywhere</span>
                        </button>
                        {kind === "page" && (
                            <button type="button" className="m-share-card" onClick={openSlack}>
                                <Slack size={20} />
                                <span className="m-share-card-label">Slack</span>
                                <span className="m-share-card-sub">Send to a channel</span>
                            </button>
                        )}
                        {kind === "page" && (
                            <button type="button" className="m-share-card" onClick={handlePrint}>
                                <Printer size={20} />
                                <span className="m-share-card-label">Save as PDF</span>
                                <span className="m-share-card-sub">Via browser print</span>
                            </button>
                        )}
                        {kind === "page" && entityId && (
                            <button type="button" className="m-share-card" onClick={() => handleDownload(`/api/manuals/pages/${entityId}/export.md`)}>
                                <FileDown size={20} />
                                <span className="m-share-card-label">Download Markdown</span>
                                <span className="m-share-card-sub">Page body as .md</span>
                            </button>
                        )}
                        {kind === "category" && entityId && (
                            <button type="button" className="m-share-card" onClick={() => handleDownload(`/api/manuals/categories/${entityId}/export.zip`)}>
                                <FileArchive size={20} />
                                <span className="m-share-card-label">Download ZIP</span>
                                <span className="m-share-card-sub">All pages as Markdown</span>
                            </button>
                        )}
                    </div>
                )}

                {panel === "slack" && (
                    <div className="m-slack-share">
                        {loadingRecipients && <div className="m-slack-empty">Loading channels…</div>}
                        {!loadingRecipients && slackConfigured === false && (
                            <div className="m-slack-empty">
                                Slack isn't configured for this workspace yet. Ask an administrator to set the bot token.
                            </div>
                        )}
                        {!loadingRecipients && slackConfigured && (
                            <>
                                <label className="m-slack-label">Share with</label>
                                <div className="m-slack-picker" ref={pickerRef}>
                                    <button
                                        type="button"
                                        className="m-slack-picker-trigger"
                                        onClick={() => setPickerOpen(o => !o)}
                                    >
                                        {selectedRecipient ? (
                                            <span className="m-slack-picker-selected">
                                                <span className="m-slack-recipient-icon">
                                                    {selectedRecipient.type === "user"
                                                        ? <AtSign size={14} />
                                                        : selectedRecipient.isPrivate
                                                            ? <Lock size={14} />
                                                            : <Hash size={14} />}
                                                </span>
                                                <span>{selectedRecipient.displayName.replace(/^[@#]/, "")}</span>
                                            </span>
                                        ) : (
                                            <span className="m-slack-picker-placeholder">Select Slack conversation…</span>
                                        )}
                                        <ChevronDown size={16} />
                                    </button>
                                    {pickerOpen && (
                                        <div className="m-slack-picker-dropdown">
                                            <input
                                                autoFocus
                                                type="text"
                                                className="m-slack-picker-search"
                                                placeholder="Search channels and people…"
                                                value={recipientFilter}
                                                onChange={e => setRecipientFilter(e.target.value)}
                                            />
                                            <div className="m-slack-picker-list">
                                                {filteredRecipients.length === 0 && (
                                                    <div className="m-slack-empty">No matches</div>
                                                )}
                                                {channelGroup.length > 0 && (
                                                    <>
                                                        <div className="m-slack-picker-group">Channels</div>
                                                        {channelGroup.map(renderRecipientRow)}
                                                    </>
                                                )}
                                                {privateChannelGroup.length > 0 && (
                                                    <>
                                                        <div className="m-slack-picker-group">Private channels</div>
                                                        {privateChannelGroup.map(renderRecipientRow)}
                                                    </>
                                                )}
                                                {userGroup.length > 0 && (
                                                    <>
                                                        <div className="m-slack-picker-group">People</div>
                                                        {userGroup.map(renderRecipientRow)}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <label className="m-slack-label" style={{ marginTop: 14 }}>Your message</label>
                                <textarea
                                    className="m-slack-message"
                                    placeholder="optional"
                                    rows={3}
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                />

                                {externalExposure && (
                                    <div className="m-external-warning" style={{ marginTop: 12 }}>
                                        <AlertTriangle size={13} />
                                        <span>
                                            {selectedRecipient?.isExternal
                                                ? <>{selectedRecipient.displayName.replace(/^[@#]/, "")} is outside your organization. Anything you share will be visible to that external organization.</>
                                                : <>This channel includes people outside of the organization. Anything you share will be visible to them.</>}
                                            {awaitingExternalConfirm && " Click “Share externally” to confirm."}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {awaitingExternalConfirm ? (
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => setAwaitingExternalConfirm(false)}>Cancel</button>
                ) : (
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Close</button>
                )}
                {panel === "slack" && slackConfigured && (
                    <button
                        type="button"
                        className={`m-btn ${awaitingExternalConfirm ? "m-btn-danger" : "m-btn-primary"}`}
                        disabled={!selectedRecipient || sharing}
                        onClick={handleShareSlack}
                    >
                        {sharing ? "Sharing…" : awaitingExternalConfirm ? "Share externally" : "Share page"}
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
